import {
  type FragmentOf,
  graphql,
  type ResultOf,
  readFragment,
} from "@opensea/graphql"
import { create } from "zustand"
import { mutative } from "zustand-mutative"
import { currencyIdentifierFragment } from "@/components/currencies/utils"
import { isBulkActionEnabled } from "@/constants/constants"
import { useBuyItemsFragment } from "../use-buy-items.fragment"
import { useMakeOfferFragment } from "../use-make-offer.fragment"
import {
  isOwnItemListing,
  isOwnItemListingFragment,
} from "../validation/listing"
import { isItemTradable, isItemTradableFragment } from "../validation/tradable"
import { MAX_ITEMS_FOR_SELECTION } from "./constants"
import type { CollectionItemsActionState } from "./types"

export const collectionItemsPurchaseSelectionFragment = graphql(
  `
    fragment collectionItemsPurchaseSelection on Item {
      id
      imageUrl
      bestListing {
        pricePerItem {
          token {
            unit
            symbol
            ...currencyIdentifier
          }
          usd
        }
      }
      collection {
        slug
      }
      isFungible
      ...useBuyItems
      ...useMakeOffer
      ...isItemTradable
      ...isOwnItemListing
    }
  `,
  [
    currencyIdentifierFragment,
    useBuyItemsFragment,
    useMakeOfferFragment,
    isItemTradableFragment,
    isOwnItemListingFragment,
  ],
)

export type CollectionItemsPurchaseSelectionState =
  CollectionItemsActionState & {
    // Used to hold all of the search results
    allItems: ResultOf<typeof collectionItemsPurchaseSelectionFragment>[]
    eligibleSelectionItems: ResultOf<
      typeof collectionItemsPurchaseSelectionFragment
    >[]
    eligibleSelectionItemsMap: Map<
      string,
      ResultOf<typeof collectionItemsPurchaseSelectionFragment>
    >

    maxPricePerItem: number
    manuallySetMaxPricePerItem: boolean
    selectedItems: Map<
      string,
      ResultOf<typeof collectionItemsPurchaseSelectionFragment> & {
        quantity: number
      }
    >
  }

type CollectionItemsPurchaseSelectionActions = {
  setDesiredNumberOfItems: (priceInUsd: boolean, num?: number) => void
  setMaxPricePerItem: (maxPricePerItem?: number) => void
  setAllItems: (
    items: FragmentOf<typeof collectionItemsPurchaseSelectionFragment>[],
  ) => void

  // Used when the user bulk selects all items
  selectAllItems: () => void
  getTotalSelectedQuantity: () => number
  isItemSelectable: (itemId: string) => boolean
  selectItem: (itemId: string, selectionState?: boolean) => void
  resetSelections: () => void
  updateSelectionQuantity: (itemId: string, quantity: number) => void
  updateItemSelections: () => void
}

export type CollectionItemsPurchaseSelectionStore =
  CollectionItemsPurchaseSelectionState &
    CollectionItemsPurchaseSelectionActions

const DEFAULT_PURCHASE_STATE: CollectionItemsPurchaseSelectionState = {
  mode: "automatic",
  allItems: [],
  eligibleSelectionItems: [],
  eligibleSelectionItemsMap: new Map(),

  selectedItems: new Map(),
  desiredNumberOfItems: 0,
  maxPricePerItem: 0,
  manuallySetMaxPricePerItem: false,
}

export const createCollectionItemsPurchaseSelectionStore = () => {
  return create<CollectionItemsPurchaseSelectionStore>()(
    mutative((set, get) => ({
      ...DEFAULT_PURCHASE_STATE,

      setDesiredNumberOfItems: (priceInUsd: boolean, num?: number) => {
        set(state => {
          const desiredNumberOfItems = num
            ? Math.min(Math.max(0, num), MAX_ITEMS_FOR_SELECTION)
            : 0

          state.desiredNumberOfItems = desiredNumberOfItems
          if (num && state.mode === "automatic") {
            const newSelectedItems = state.eligibleSelectionItems.slice(
              0,
              desiredNumberOfItems,
            )
            state.selectedItems = new Map(
              newSelectedItems.map(item => [item.id, { ...item, quantity: 1 }]),
            )

            // if price was not yet set manually, set the max price per item to the highest price among
            // the selected items + 10%
            if (!state.manuallySetMaxPricePerItem) {
              const maxPricePerItem =
                newSelectedItems.reduce((acc, item) => {
                  const tokenPrice =
                    item.bestListing?.pricePerItem.token.unit ?? 0
                  const usdPrice = item.bestListing?.pricePerItem.usd ?? 0
                  return Math.max(acc, priceInUsd ? usdPrice : tokenPrice)
                }, 0) * 1.1

              state.maxPricePerItem = Number(maxPricePerItem.toPrecision(4))
            }
          } else if (!num) {
            state.selectedItems = new Map()

            // if price was not yet set manually, set the price back to zero
            if (
              !state.manuallySetMaxPricePerItem &&
              state.mode === "automatic"
            ) {
              state.maxPricePerItem = 0
            }
          }
        })
      },

      isItemSelectable: (id: string) => {
        const selectedItems = get().selectedItems
        const selectedItemCount = selectedItems.size
        const item = get().eligibleSelectionItemsMap.get(id)
        const multipleItemsSelectedWithBulkActionDisabled =
          [...selectedItems.values()].filter(
            selectedItem => !isBulkActionEnabled(selectedItem.collection?.slug),
          ).length >= 1
        return (
          ((Boolean(item) && selectedItemCount < MAX_ITEMS_FOR_SELECTION) ||
            selectedItems.has(id)) &&
          !multipleItemsSelectedWithBulkActionDisabled
        )
      },

      getTotalSelectedQuantity: () => {
        return Array.from(get().selectedItems.values()).reduce(
          (total, item) => total + item.quantity,
          0,
        )
      },

      // This method should only be called when manually selecting individual items ie not sweeps
      selectItem: (id: string, selectionState?: boolean) => {
        set(state => {
          const item = state.eligibleSelectionItemsMap.get(id)

          if (
            !item ||
            (state.selectedItems.size >= MAX_ITEMS_FOR_SELECTION &&
              !state.selectedItems.has(id))
          ) {
            return
          }

          state.mode = "manual"

          if (selectionState !== undefined) {
            if (selectionState) {
              state.selectedItems.set(id, { ...item, quantity: 1 })
            } else {
              state.selectedItems.delete(id)
            }
            return
          }

          if (state.selectedItems.has(id)) {
            state.selectedItems.delete(id)
          } else {
            state.selectedItems.set(id, { ...item, quantity: 1 })
          }

          if (state.selectedItems.size === 0) {
            state.mode = "automatic"
          }
        })
      },

      setAllItems: (
        items: FragmentOf<typeof collectionItemsPurchaseSelectionFragment>[],
        currentAddress?: string,
      ) => {
        set(state => {
          const allItems = items.map(item =>
            readFragment(collectionItemsPurchaseSelectionFragment, item),
          )
          state.allItems = allItems
          // Allow selection of items that are either:
          // 1. Listed and tradable (for buying)
          // 2. Not listed but tradable (for making offers)
          // Exclude items that are owned by the current user's own listings
          state.eligibleSelectionItems = allItems.filter(
            item =>
              isItemTradable(item) &&
              (item.isFungible || !isOwnItemListing(item, currentAddress)),
          )

          state.eligibleSelectionItemsMap = new Map(
            state.eligibleSelectionItems.map(item => [item.id, item]),
          )
        })
      },

      updateItemSelections: () => {
        set(state => {
          if (state.mode === "automatic" && state.desiredNumberOfItems) {
            const newSelectedItems = state.eligibleSelectionItems.slice(
              0,
              state.desiredNumberOfItems,
            )
            state.selectedItems = new Map(
              newSelectedItems.map(item => [item.id, { ...item, quantity: 1 }]),
            )
          } else if (state.selectedItems.size) {
            // make sure all items in state.selectedItems still exist in state.eligibleSelectionItems
            // if not, remove them
            const itemsToRemove = Array.from(
              state.selectedItems.values(),
            ).flatMap(item => {
              if (!state.eligibleSelectionItemsMap.has(item.id)) {
                return [item.id]
              }
              return []
            })

            if (itemsToRemove.length) {
              const newSelectedItems = new Map(state.selectedItems)
              itemsToRemove.forEach(itemId => newSelectedItems.delete(itemId))
              state.selectedItems = newSelectedItems
            }
          }
        })
      },

      selectAllItems: () => {
        set(state => {
          if (state.eligibleSelectionItems.length === 0) {
            return
          }
          if (state.selectedItems.size > 0) {
            state.selectedItems = new Map()
            state.mode = "automatic"
          } else {
            state.selectedItems = new Map(
              state.eligibleSelectionItems
                .slice(0, MAX_ITEMS_FOR_SELECTION)
                .map(item => [item.id, { ...item, quantity: 1 }]),
            )
            state.mode = "manual"
          }
        })
      },

      setMaxPricePerItem: (maxPricePerItem?: number) => {
        set({
          maxPricePerItem,
          manuallySetMaxPricePerItem:
            maxPricePerItem !== undefined && maxPricePerItem > 0,
        })
      },

      updateSelectionQuantity: (itemId: string, quantity: number) => {
        set(state => {
          const item = state.selectedItems.get(itemId)
          if (item) {
            if (quantity) {
              state.selectedItems.set(itemId, { ...item, quantity })
            } else {
              const currentSize = state.selectedItems.size
              state.selectedItems.delete(itemId)
              // When removing an item in sweep mode, we should switch back to manual selection mode
              if (state.mode === "automatic") {
                state.mode = "manual"
              } else if (currentSize === 1) {
                // When removing the last item in manual mode, we should switch back to automatic selection mode
                state.mode = "automatic"
                state.desiredNumberOfItems = 0
              }
            }
          }
        })
      },

      resetSelections: () => {
        set({
          mode: "automatic",
          selectedItems: new Map(),
          desiredNumberOfItems: 0,
          maxPricePerItem: 0,
          manuallySetMaxPricePerItem: false,
        })
      },
    })),
  )
}
